import { Box, IconButton, Slider, Typography } from '@mui/material';
import VolumeUpRounded from '@mui/icons-material/VolumeUpRounded';
import VolumeOffRounded from '@mui/icons-material/VolumeOffRounded';
import { useDispatch, useSelector } from 'react-redux';
import {
  MAX_VOLUME,
  MIN_VOLUME,
  VOLUME_CHANNELS,
  VOLUME_CHANNEL_LABELS,
  VolumeChannel,
} from './volumeSettings';
import { selectVolumeSettings, setChannelMuted, setChannelVolume } from './volumeSlice';
import { volumeControlsAvailable } from './volumeAvailability';
import { TOUCH_TARGET_MIN_SIZE_PX } from 'Utils/touchTarget';

// A small slider's 2px track; MUI pads it to a 42px touch strip on a touch screen.
const SLIDER_TRACK_HEIGHT_PX = 2;
const sliderTouchPaddingPx = (TOUCH_TARGET_MIN_SIZE_PX - SLIDER_TRACK_HEIGHT_PX) / 2;
// MUI's own touch strip is 42px tall; ours is 44, and the extra 1px each side is given back.
const MUI_SLIDER_TOUCH_HEIGHT_PX = 42;

// The mute button with a mouse: a 20px icon in 2px of padding, in a 28px column.
const MUTE_BUTTON_WIDTH_PX = 28;
const MUTE_BUTTON_HEIGHT_PX = 24;

const sliderSx = {
  color: '#6F87F1',
  '& .MuiSlider-thumb': { width: 16, height: 16 },
  '& .MuiSlider-rail': { color: '#525969', opacity: 1 },
  // On a touch screen the slider answers a 44px strip (the whole track, 44px tall) and its thumb a
  // 44px circle; it looks the same and the row keeps its height. The thumb's hidden range input
  // (what assistive tech and touch checks read as the control) spans the thumb's touch circle.
  '@media (pointer: coarse)': {
    paddingBlock: `${sliderTouchPaddingPx}px`,
    marginBlock: `${(MUI_SLIDER_TOUCH_HEIGHT_PX - TOUCH_TARGET_MIN_SIZE_PX) / 2}px`,
    '& .MuiSlider-thumb::after': {
      width: TOUCH_TARGET_MIN_SIZE_PX,
      height: TOUCH_TARGET_MIN_SIZE_PX,
    },
    // MUI sizes the input inline (100% of the thumb), so only an important rule can widen it.
    '& .MuiSlider-thumb input': {
      width: `${TOUCH_TARGET_MIN_SIZE_PX}px !important`,
      height: `${TOUCH_TARGET_MIN_SIZE_PX}px !important`,
      margin: '0 !important',
      top: `calc(50% - ${TOUCH_TARGET_MIN_SIZE_PX / 2}px)`,
      left: `calc(50% - ${TOUCH_TARGET_MIN_SIZE_PX / 2}px)`,
    },
  },
} as const;

// On a touch screen the mute button is a 44px square; the negative margin gives the extra back on
// every side, so its column and row keep their size and nothing moves.
const muteButtonTouchSx = {
  '@media (pointer: coarse)': {
    minWidth: `${TOUCH_TARGET_MIN_SIZE_PX}px`,
    minHeight: `${TOUCH_TARGET_MIN_SIZE_PX}px`,
    marginInline: `${(MUTE_BUTTON_WIDTH_PX - TOUCH_TARGET_MIN_SIZE_PX) / 2}px`,
    marginBlock: `${(MUTE_BUTTON_HEIGHT_PX - TOUCH_TARGET_MIN_SIZE_PX) / 2}px`,
  },
} as const;

type VolumeControlsProps = {
  /** A heading above the controls, for surfaces that do not label the section. */
  title?: string;
};

/**
 * One row per sound channel (piano, metronome, narration, sound effects): a
 * level slider and that channel's own mute. There is no mute over every
 * channel. Every surface that shows these renders this one component, so they
 * all edit the same settings.
 */
export default function VolumeControls({ title }: VolumeControlsProps) {
  const dispatch = useDispatch();
  const settings = useSelector(selectVolumeSettings);

  if (!volumeControlsAvailable()) return null;

  const onVolumeChange = (channel: VolumeChannel) => (_event: Event, value: number | number[]) =>
    dispatch(setChannelVolume({ channel, volume: Array.isArray(value) ? value[0] : value }));

  return (
    <Box
      data-testid="volume-controls"
      sx={{ display: 'flex', flexDirection: 'column', gap: '4px', color: '#ECECED' }}
    >
      {title && (
        <Typography variant="body1" sx={{ fontFamily: 'Lato', marginBottom: '4px' }}>
          {title}
        </Typography>
      )}
      {VOLUME_CHANNELS.map((channel) => {
        const { level, muted } = settings[channel];
        const label = VOLUME_CHANNEL_LABELS[channel];
        return (
          <Box
            key={channel}
            sx={{
              display: 'grid',
              gridTemplateColumns: '96px minmax(100px, 1fr) 36px 28px',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Typography variant="body2" sx={{ fontFamily: 'Lato' }}>
              {label}
            </Typography>
            <Slider
              size="small"
              sx={{ ...sliderSx, opacity: muted ? 0.5 : 1 }}
              min={MIN_VOLUME}
              max={MAX_VOLUME}
              step={1}
              value={level}
              onChange={onVolumeChange(channel)}
              slotProps={{ input: { 'aria-label': `${label} volume` } }}
            />
            <Typography
              variant="body2"
              sx={{ fontFamily: 'Lato', textAlign: 'right', opacity: muted ? 0.5 : 1 }}
            >
              {level}%
            </Typography>
            <IconButton
              size="small"
              aria-label={`Mute ${label}`}
              aria-pressed={muted}
              onClick={() => dispatch(setChannelMuted({ channel, muted: !muted }))}
              sx={{ color: muted ? '#F2A0A0' : '#ECECED', padding: '2px', ...muteButtonTouchSx }}
            >
              {muted ? <VolumeOffRounded fontSize="small" /> : <VolumeUpRounded fontSize="small" />}
            </IconButton>
          </Box>
        );
      })}
    </Box>
  );
}
